import { Component, OnInit, ViewChild } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';

import { AgentSpamQueueApiService } from 'src/app/services/agent-spam-queue-api.service';
import {
  AgentSpamQueueItem,
  AgentSpamSenderItem,
  AgentSpamQueueResolveAction,
  AGENT_SPAM_QUEUE_RESOLVE_ACTIONS,
} from 'src/app/shared/agent-spam-queue';

/**
 * AG-10a review queue -- the "spam section" the SPAM addendum called for but
 * Phase 7 shipped without. Embedded as its own accordion section on
 * `HomeComponent`, sibling to `app-agent-dashboard` (same `canSeeAgentDashboard`
 * / `portal-admin` gate, same "not a standalone routed page" reasoning that
 * component's own header already documents) -- kept as a separate component
 * rather than folded into `AgentDashboardComponent`, matching the backend's
 * own `AgentSpamQueue.ts`/`AgentDashboard.ts` split: that component's narrow
 * "Spam" button only flips a status, this one is the actual review surface
 * over `AgentSpamQueue`/`AgentSpamSenders`.
 *
 * Two independent tables, two independent loads: the pending review queue
 * (resolve to discarded/released/quarantined) and the auto-block sender
 * list (unblock). Neither depends on the other's state.
 */
@Component({
  selector: 'app-agent-spam-queue',
  templateUrl: './agent-spam-queue.component.html',
  styleUrls: ['./agent-spam-queue.component.css']
})
export class AgentSpamQueueComponent implements OnInit {

  queue: AgentSpamQueueItem[] = [];
  queueLoaded = false;

  @ViewChild('ptableQueue') ptableQueue: Table;
  /** Per-column filter row values, same `cfN`-per-table convention every other Home
   *  accordion table already uses (e.g. `home-recent-orders-list.component.ts`'s `cf1`). */
  cfQueue: any = {};

  /** Single-select: currently selected row in the queue table, null if none selected. */
  selectedQueueItem: AgentSpamQueueItem | null = null;

  resolveActions = AGENT_SPAM_QUEUE_RESOLVE_ACTIONS;
  /** Single resolve-action selection (applies to the currently selected queue item only). */
  selectedResolveAction: AgentSpamQueueResolveAction | null = null;
  /** Keyed by AgentSpamQueue.id -- disables a row's resolve button while its
   *  own call is in flight. */
  queueActionInProgress: Record<number, boolean> = {};

  senders: AgentSpamSenderItem[] = [];
  sendersLoaded = false;
  sendersAutoBlockOnly = true;
  /** Single-select: currently selected row in the senders table, null if none selected. */
  selectedSender: AgentSpamSenderItem | null = null;
  /** Keyed by AgentSpamSenders.id. */
  senderActionInProgress: Record<number, boolean> = {};

  /** Action-bar enablement for both tables. Each bar renders its controls at all
   *  times and these decide only whether they are clickable, so a bar never
   *  changes shape as the selection moves -- see the templates' own notes. */
  get queueSelectionBusy(): boolean {
    return !!this.selectedQueueItem && !!this.queueActionInProgress[this.selectedQueueItem.id];
  }

  get canResolveSelected(): boolean {
    return !!this.selectedQueueItem && !!this.selectedResolveAction && !this.queueSelectionBusy;
  }

  get senderSelectionBusy(): boolean {
    return !!this.selectedSender && !!this.senderActionInProgress[this.selectedSender.id];
  }

  /** Unblock only makes sense for a sender that is actually auto-blocked --
   *  the same condition the per-row button used as its `*ngIf`. */
  get canUnblockSelected(): boolean {
    return !!this.selectedSender && !!this.selectedSender.autoBlock && !this.senderSelectionBusy;
  }

  @ViewChild('ptableSenders') ptableSenders: Table;
  cfSenders: any = {};

  constructor(
    private spamQueueApi: AgentSpamQueueApiService,
    private messageService: MessageService,
  ) {
  }

  ngOnInit(): void {
    this.loadQueue();
    this.loadSenders();
  }

  loadQueue(): void {
    this.queueLoaded = false;
    this.spamQueueApi.getQueue('pending').subscribe({
      next: items => {
        this.queue = items;
        this.queueLoaded = true;
      },
      error: err => {
        console.error('*** AgentSpamQueueComponent loadQueue: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося завантажити чергу спаму', detail: err?.error?.message || err?.message || 'Unknown error' });
        this.queueLoaded = true;
      }
    });
  }

  /** Gated by `selectedResolveAction` being set, same as the
   *  dashboard's `onIgnore()`. */
  onResolve(item: AgentSpamQueueItem): void {
    const action = this.selectedResolveAction;
    if (!action) { return; }

    this.queueActionInProgress[item.id] = true;
    this.spamQueueApi.resolveQueueItem(item.id, action).subscribe({
      next: () => {
        this.queueActionInProgress[item.id] = false;
        this.selectedResolveAction = null;
        this.selectedQueueItem = null;
        const label = this.resolveActions.find(a => a.value === action)?.title ?? action;
        this.messageService.add({ severity: 'success', summary: `Запис #${item.id}: ${label}` });
        this.loadQueue();
      },
      error: err => {
        this.queueActionInProgress[item.id] = false;
        console.error('*** AgentSpamQueueComponent onResolve: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося обробити запис', detail: err?.error?.message || err?.message || 'Unknown error' });
      }
    });
  }

  loadSenders(): void {
    this.sendersLoaded = false;
    this.spamQueueApi.getSenders(this.sendersAutoBlockOnly).subscribe({
      next: items => {
        this.senders = items;
        this.sendersLoaded = true;
      },
      error: err => {
        console.error('*** AgentSpamQueueComponent loadSenders: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося завантажити відправників', detail: err?.error?.message || err?.message || 'Unknown error' });
        this.sendersLoaded = true;
      }
    });
  }

  onAutoBlockFilterChange(): void {
    this.loadSenders();
  }

  onUnblock(sender: AgentSpamSenderItem): void {
    this.senderActionInProgress[sender.id] = true;
    this.spamQueueApi.unblockSender(sender.id).subscribe({
      next: () => {
        this.senderActionInProgress[sender.id] = false;
        this.selectedSender = null;
        this.messageService.add({ severity: 'success', summary: `Відправника ${sender.senderEmail} розблоковано` });
        this.loadSenders();
      },
      error: err => {
        this.senderActionInProgress[sender.id] = false;
        console.error('*** AgentSpamQueueComponent onUnblock: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося розблокувати', detail: err?.error?.message || err?.message || 'Unknown error' });
      }
    });
  }
}
